import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

export interface QueryParamType<T> {
    converter: (paramValue: string) => T | null;
}

export const StringValue: QueryParamType<string> = {
    converter: (paramValue) => encodeURI(paramValue)
};

export const StringArrayValue: QueryParamType<string[]> = {
    converter: (paramValue) => (!!paramValue ? encodeURI(paramValue).split(',') : [])
};

export const NumericValue: QueryParamType<number> = {
    converter: (paramValue) => (!!paramValue ? Number(paramValue) : null)
};

type UseQueryParamOptions<T> = {
    paramType: QueryParamType<T>;
    defaultValue: T;
    replaceNavigation: boolean;
};

let globalStoredSearchParams: URLSearchParams; // Used to store mutations of the Search Params before it actually applied to the URI

export const useQueryParam = <T extends string[] | string | number | null>(
    key: string,
    options: UseQueryParamOptions<T>
): [T, (newQuery: T | undefined) => void] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const paramValue = searchParams.get(key);

    globalStoredSearchParams = searchParams;

    const value = useMemo(() => {
        if (paramValue) {
            return options.paramType.converter(paramValue) ?? options.defaultValue;
        }
        return options.defaultValue;
    }, [paramValue]);

    const setValue = useCallback(
        (newValue: T | undefined) => {
            let newSearchParams = new URLSearchParams(globalStoredSearchParams);

            if (newValue === undefined) {
                newSearchParams.delete(key);
            } else {
                newSearchParams.set(key, newValue?.toString() ?? '');
            }

            globalStoredSearchParams = newSearchParams;
            setSearchParams(newSearchParams, {replace: options.replaceNavigation});
        },
        [key, globalStoredSearchParams, setSearchParams]
    );

    return [value, setValue];
};
